import React from "react";
import { Navigate } from "react-router-dom";
import { UserStatus } from "../services/User";
import { useBoundStore } from "../store";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  // Subscribe to the credential state directly (not the selector functions) so
  // that clearing tokens — e.g. on a failed refresh — reactively redirects to
  // login instead of leaving the user on a stale protected page.
  const accessToken = useBoundStore((state) => state.accessToken);
  const user = useBoundStore((state) => state.user);

  // Unconfirmed accounts have no password yet, so they can't hold a session:
  // treat a stale persisted one as signed out
  if (!accessToken || (user && user.status === UserStatus.UNCONFIRMED)) {
    // Redirect to login page if not authenticated
    return <Navigate to="/login" replace />;
  }

  // Check subscription status (exempt users bypass this check).
  // Only redirect if we explicitly know the user is NOT exempt AND NOT active.
  // If isExemptFromSubscription is undefined (stale persisted state), do NOT redirect -
  // the user data will be refreshed by the app and the correct state will be applied.
  if (
    user &&
    user.isExemptFromSubscription === false &&
    user.isSubscriptionActive === false
  ) {
    return <Navigate to="/subscription-expired" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
